/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  isArrowDownKey,
  isArrowLeftKey,
  isArrowNavigationKey,
  isArrowRightKey,
  isArrowUpKey,
  isHorizontalArrowNavigationKey,
} from '@common/tech-shared';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  QueryList,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { uniqueId } from 'lodash-es';
import { debounceTime, merge } from 'rxjs';
import { TabButtonComponent } from './tab-button/tab-button.component';
import { TabPanelComponent } from './tab-panel/tab-panel.component';

const TAB_BUTTON_PREFIX = 'tab-button-';
const TAB_PANEL_PREFIX = 'tab-panel-';

@Component({
  selector: 'ods-tabs',
  template: `<div
      #tablist
      class="border-grayborder divide-grayborder flex h-fit flex-1/3 flex-col divide-y rounded-md border md:min-w-72"
      role="tablist"
      [attr.aria-label]="ariaLabel"
      aria-orientation="vertical"
      (keydown)="onKeydown($event)"
    ></div>
    <div #hidden class="hidden"><ng-content /></div>`,
  host: { class: 'flex md:flex-row flex-col gap-4' },
})
export class TabsComponent implements AfterContentInit {
  @Input({ required: true }) ariaLabel: string;
  @Input() initialTabIndex: number = 0;

  @ViewChild('tablist', { static: true }) tablistRef!: ElementRef<HTMLElement>;
  @ViewChild('hidden', { static: true }) hiddenRef!: ElementRef<HTMLElement>;

  @ContentChildren(TabButtonComponent, { descendants: true }) tabButtons!: QueryList<TabButtonComponent>;
  @ContentChildren(TabPanelComponent, { descendants: true }) tabPanels!: QueryList<TabPanelComponent>;

  activeTabIndex: number = 0;

  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  ngAfterContentInit(): void {
    this.organizeContent();
    this.setupTabs();
    merge(this.tabButtons.changes, this.tabPanels.changes)
      .pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleContentChanges());
  }

  handleContentChanges(): void {
    this.activeTabIndex = this.initialTabIndex;
    this.organizeContent();
    if (this.tabButtons.length === this.tabPanels.length) {
      this.setupTabs();
    }
  }

  organizeContent(): void {
    this.tabButtons.forEach((button) => {
      this.tablistRef.nativeElement.appendChild(button.ref.nativeElement);
    });
    this.tabPanels.forEach((panel) => {
      this.elementRef.nativeElement.insertBefore(panel.ref.nativeElement, this.hiddenRef.nativeElement);
    });
  }

  setupTabs(): void {
    this.initializeTabs();
    if (this.hasPanels()) this._showPanel(this.clampActiveTabIndex());
  }

  clampActiveTabIndex(): number {
    return Math.min(this.activeTabIndex, this.tabButtons.length - 1);
  }

  initializeTabs(): void {
    if (this.tabPanels.length !== this.tabButtons.length) {
      console.error("Tabs component: Buttons' length is not equal to panels' length!");
      return;
    }

    for (let index = 0; index < this.tabButtons.length; index++) {
      const uniqId: string = uniqueId();
      this.setPanelProps(this.tabPanels.get(index), uniqId);
      this.setButtonProps(this.tabButtons.get(index), uniqId);
    }
  }

  setPanelProps(panel: TabPanelComponent, id: string): void {
    panel.id.set(TAB_PANEL_PREFIX + id);
    panel.buttonId.set(TAB_BUTTON_PREFIX + id);
  }

  setButtonProps(button: TabButtonComponent, id: string): void {
    button.id.set(TAB_BUTTON_PREFIX + id);
    button.panelId.set(TAB_PANEL_PREFIX + id);
    button.onClick = this.createActivatePanelHandler(TAB_PANEL_PREFIX + id);
  }

  hasPanels(): boolean {
    return this.tabPanels.length > 0;
  }

  onKeydown(e: KeyboardEvent): void {
    if (isArrowNavigationKey(e) || isHorizontalArrowNavigationKey(e)) this.handleArrowNavigation(e);
  }

  _showPanel(index: number): void {
    this.activeTabIndex = index;
    this.tabPanels.forEach((panel) => panel.show.set(false));
    this.tabButtons.forEach((button) => button.active.set(false));

    this.tabPanels.get(index).show.set(true);
    this.tabButtons.get(index).active.set(true);
  }

  createActivatePanelHandler(selectedId: string): () => void {
    return () => {
      this.updateTabPanels(selectedId);
      this.updateTabButtons(selectedId);
    };
  }

  updateTabPanels(selectedId: string): void {
    this.tabPanels.forEach((panel, index) => {
      this.updateTabPanel(panel, selectedId);
      if (panel.id() === selectedId) this.setActiveTabIndex(index);
    });
  }

  updateTabPanel(panel: TabPanelComponent, selectedId: string): void {
    panel.show.set(panel.id() === selectedId);
  }

  setActiveTabIndex(panelIndex: number): void {
    this.activeTabIndex = panelIndex;
  }

  updateTabButtons(selectedId: string): void {
    this.tabButtons.forEach((button) => this.updateTabButton(button, selectedId));
  }

  updateTabButton(button: TabButtonComponent, selectedId: string): void {
    button.active.set(button.panelId() === selectedId);
  }

  handleArrowNavigation(e: KeyboardEvent): void {
    e.preventDefault();
    if (!this.hasPanels()) return;

    const totalTabs: number = this.tabButtons.length;
    if (isArrowDownKey(e) || isArrowRightKey(e)) this.moveToNextTab(totalTabs);
    else if (isArrowUpKey(e) || isArrowLeftKey(e)) this.moveToPreviousTab(totalTabs);
    else return;

    this.activateCurrentTab();
  }

  moveToNextTab(totalTabs: number): void {
    this.activeTabIndex = (this.activeTabIndex + 1) % totalTabs;
  }

  moveToPreviousTab(totalTabs: number): void {
    this.activeTabIndex = (this.activeTabIndex - 1 + totalTabs) % totalTabs;
  }

  activateCurrentTab(): void {
    const buttonToActivate: TabButtonComponent = this.tabButtons.get(this.activeTabIndex);
    if (buttonToActivate) buttonToActivate.onClick();
  }
}
